import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Typography,
  AvatarGroup,
  Tooltip,
} from '@mui/material';
import {
  ChatCircle as ChatCircleIcon,
  Clock as ClockIcon,
  User as UserIcon,
  Wrench as WrenchIcon,
  UserPlus as UserPlusIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { Request_Status, type Request } from '@/types/v2/request';
import { getRequestsByUnit } from '@/api/requests';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { getInitials } from '@/utils/get-initials';
import { toTitleCase } from '@/utils/to-title-case';
import { RequestType } from '@/types/v2/request-type';
import { useDialog } from '@/hooks/v2/use-dialog';
import CreateRequestDialog from '@/pages/v2/requests/request/components/create-request-dialog';

interface UnitRequestsProps {
  unitId: string;
  communityId: string;
}

export function UnitRequests({ unitId, communityId }: UnitRequestsProps): React.JSX.Element {
  const navigate = useNavigate();
  const createRequestDialog = useDialog();
  const { data: requests = [], isLoading, error, refetch } = useQuery({
    queryKey: ['unit-requests', unitId, communityId],
    queryFn: () => getRequestsByUnit(communityId, unitId),
    enabled: Boolean(unitId) && Boolean(communityId),
  });

  const handleRowClick = (row: any) => {
    const requestId = row.original.id;
    navigate(`/requests/${requestId}`);
  };

  const handleCreateRequestClose = () => {
    createRequestDialog.handleClose();
    refetch(); // Refresh the unit requests data
  };



  const columns = React.useMemo<MRT_ColumnDef<Request>[]>(
    () => [
      {
        accessorKey: 'summary',
        header: 'Summary',
        size: 300,
        Cell: ({ row }) => (
          <Typography sx={{ fontWeight: 500 }} variant="body2">
            {row.original.summary}
          </Typography>
        ),
      },
      {
        accessorKey: 'request_type',
        header: 'Type',
        size: 150,
        Cell: ({ row }) => {
          const requestType = row.original.request_type;
          if (!requestType) return <Typography variant="body2">-</Typography>;
          const iconSelector = (requestTypeName: string) => {
            switch (requestTypeName) {
              case 'maintenance':
                return <WrenchIcon size={16} />;
              case 'repair':
                return <WrenchIcon size={16} />;
              default:
                return <WrenchIcon size={16} />;
            }
          };
          return (
            <Chip
              color="primary"
              icon={iconSelector(requestType.name)}
              label={toTitleCase(requestType.name)}
              size="small"
              sx={{
                '& .MuiChip-icon': {
                  marginRight: 0.5,
                },
              }}
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'request_status',
        header: 'Status',
        size: 150,
        Cell: ({ row }) => {
          const status = row.original.request_status;
          if (!status) return <Typography variant="body2">-</Typography>;
          
          const getStatusColor = (statusName: string) => {
            switch (statusName.toLowerCase()) {
              case 'new':
                return 'info';
              case 'initial review':
                return 'warning';
              case 'quoting':
                return 'warning';
              case 'quoted':
                return 'info';
              case 'final review':
                return 'warning';
              case 'approved':
                return 'success';
              case 'rejected':
                return 'error';
              case 'scheduled':
                return 'info';
              case 'quality review':
                return 'warning';
              case 'completed':
                return 'success';
              default:
                return 'default';
            }
          };
          
          return (
            <Chip
              color={getStatusColor(status.name) as any}
              label={toTitleCase(status.name)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: 'created_by',
        header: 'Created By',
        size: 200,
        Cell: ({ row }) => {
          const user = row.original.created_by;
          if (!user) return <Typography variant="body2">-</Typography>;
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                {getInitials(`${user.first_name} ${user.last_name}`)}
              </Avatar>
              <Typography variant="body2">
                {user.first_name && user.last_name 
                  ? `${user.first_name} ${user.last_name}`
                  : user.email
                }
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 150,
        Cell: ({ row }) => (
          <Typography color="text.secondary" variant="body2">
            {dayjs(row.original.created_at).format('MMM D, YYYY')}
          </Typography>
        ),
      },
      {
        accessorKey: 'experienced_on',
        header: 'Experienced',
        size: 150,
        Cell: ({ row }) => {
          if (!row.original.experienced_on) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography color="text.secondary" variant="body2">
              {dayjs(row.original.experienced_on).format('MMM D, YYYY')}
            </Typography>
          );
        },
      },
    ],
    []
  );

  if (error) {
    return (
      <Card>
        <CardHeader
          avatar={
            <Avatar sx={{ backgroundColor: 'error.main' }}>
              <ChatCircleIcon />
            </Avatar>
          }
          subheader="Error loading requests"
          title="Requests"
        />
        <Divider />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="body2">
            Failed to load requests. Please try again.
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        action={
          <Button
            onClick={createRequestDialog.handleOpen}
            size="small"
            startIcon={<UserPlusIcon size={18} />}
            sx={{ textTransform: 'none', fontWeight: 500 }}
            variant="contained"
          >
            Create Request
          </Button>
        }
        avatar={
          <Avatar sx={{ backgroundColor: 'primary.main' }}>
            <ChatCircleIcon />
          </Avatar>
        }
        subheader={
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography color="text.secondary" variant="body2">
              {`${requests.length} request${requests.length !== 1 ? 's' : ''} for this unit`}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              • Click any row to view request details
            </Typography>
          </Stack>
        }
        title="Requests"
      />
      <Divider />
      
      {requests.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            No requests found for this unit.
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={requests}
          enableColumnFilters={false}
          enableColumnResizing
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableMultiRowSelection={false}
          enablePagination
          enableRowActions={false}
          enableRowSelection={false}
          enableSelectAll={false}
          enableSorting
          initialState={{
            showGlobalFilter: false,
            showColumnFilters: false,
            columnPinning: {
              left: ['mrt-row-expand', 'mrt-row-select'],
            },
            sorting: [
              { id: 'created_at', desc: true },
            ],
          }}
          muiTableBodyCellProps={{
            sx: {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => { handleRowClick(row); },
            sx: {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease-in-out',
              },
            },
          })}
          muiTableContainerProps={(props) => ({
            ...props,
            sx: {
              minHeight: '400px',
              '& .MuiTable-root': {
                borderCollapse: 'separate',
                borderSpacing: 0,
              },
              '& .MuiTableHead-root .MuiTableRow-root': {
                backgroundColor: 'background.paper',
                fontWeight: 600,
                '& .MuiTableCell-root': {
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  fontWeight: 600,
                },
              },
            },
          })}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: 'background.paper',
              fontWeight: 600,
              fontSize: '0.875rem',
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
            },
          }}
          state={{
            isLoading,
          }}
        />
      )}
      
      <CreateRequestDialog 
        communityId={communityId} 
        onClose={handleCreateRequestClose}
        open={createRequestDialog.open}
        unitId={unitId}
      />
    </Card>
  );
}
